/**
 * WarmupFab — a draggable floating button that opens the Warmup daily-games hub (/app/games).
 *
 * Floats on top of every authenticated screen (rendered from the root tab layout). The user can
 * drag it anywhere (it snaps to the nearest side edge on release so it never sits over the middle
 * of the feed) and a tap opens Warmup. Tap-vs-drag is disambiguated by total finger travel.
 *
 * Two shapes: a compact icon-only circle on small screens, and an icon + "Games" pill on wider
 * ones. The button is `position:absolute` (no full-bleed wrapper) so it only claims touches inside
 * itself — the list underneath keeps scrolling normally.
 */

import { useEffect, useRef } from 'react';
import { Animated, PanResponder, Text, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Gamepad2 } from 'lucide-react-native';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';
import {
  warmupFabStyles as styles,
  WARMUP_FAB_WIDTH,
  WARMUP_FAB_HEIGHT,
  WARMUP_FAB_COMPACT_SIZE,
} from './WarmupFab.styles';

const EDGE_MARGIN = 16;
const TOP_RESERVE = 112; // clear the header + any sub-tab bar
const BOTTOM_RESERVE = 100; // clear the main bottom tab bar (TAB_BAR_HEIGHT 70 + breathing room)
const DRAG_THRESHOLD = 8; // px of travel before a press counts as a drag, not a tap
// Below this width we drop the label and show an icon-only circle (phones); at/above it we show
// the labeled pill (tablets / web).
const COMPACT_BREAKPOINT = 480;
// The scene is full-window-width but its content is a centered ~520px column (PicksView maxWidth).
// The button should live in the side margin just *outside* that column, not on top of the cards.
const CONTENT_MAX = 520;
const CARD_GAP = 12; // gap between the card column edge and the button

export function WarmupFab() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();

  // Compact (icon-only circle) on genuinely small screens, and also whenever the labeled pill is
  // too wide to sit in the side margin *outside* the card column — the circle is narrow enough to
  // clear the cards where the pill can't, so the button never lands on top of the content.
  const sideMargin = Math.max(0, (width - CONTENT_MAX) / 2);
  const pillFitsOutside = sideMargin >= WARMUP_FAB_WIDTH + CARD_GAP + EDGE_MARGIN;
  const compact = width < COMPACT_BREAKPOINT || !pillFitsOutside;
  const fabWidth = compact ? WARMUP_FAB_COMPACT_SIZE : WARMUP_FAB_WIDTH;
  const fabHeight = compact ? WARMUP_FAB_COMPACT_SIZE : WARMUP_FAB_HEIGHT;

  // Resolve the box the button is allowed to rest in (kept fresh in a ref for the PanResponder).
  // X docks flush to the screen edge (fully left / fully right); on wide screens that edge sits in
  // the margin well clear of the centered card column, and the compact fallback above keeps the
  // narrower circle at the edge (instead of an overlapping pill) when the margin is tight.
  const minX = EDGE_MARGIN;
  const maxX = width - fabWidth - EDGE_MARGIN;
  const minY = insets.top + TOP_RESERVE;
  const maxY = Math.max(minY, height - insets.bottom - BOTTOM_RESERVE - fabHeight);
  const bounds = useRef({ minX, maxX, minY, maxY });
  bounds.current = { minX, maxX, minY, maxY };

  const pan = useRef(new Animated.ValueXY({ x: maxX, y: maxY })).current;
  const last = useRef({ x: maxX, y: maxY });
  const navigate = useRef<() => void>(() => undefined);
  navigate.current = () => router.push('/app/games' as never);

  // Re-anchor to the resting corner (bottom, hugging the nearest side outside the cards) whenever
  // the viewport changes — web resize, rotation, compact toggle, or the first real measurement
  // after a 0-size initial render (which is why we reset Y to the bottom rather than trusting the
  // last value, which can be stuck at the top from that first pass).
  useEffect(() => {
    const b = bounds.current;
    const nearRight = last.current.x + fabWidth / 2 >= width / 2;
    const x = nearRight ? b.maxX : b.minX;
    const y = b.maxY;
    last.current = { x, y };
    pan.setValue({ x, y });
  }, [width, height, fabWidth, fabHeight, pan]);

  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 1800, useNativeDriver: true }),
        // Rest between ripples so it nudges the eye occasionally instead of pumping nonstop.
        Animated.delay(1600),
        Animated.timing(pulse, { toValue: 0, duration: 0, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_e, g) => Math.hypot(g.dx, g.dy) > DRAG_THRESHOLD,
      onPanResponderMove: (_e, g) => {
        const b = bounds.current;
        const x = Math.min(Math.max(last.current.x + g.dx, b.minX), b.maxX);
        const y = Math.min(Math.max(last.current.y + g.dy, b.minY), b.maxY);
        pan.setValue({ x, y });
      },
      onPanResponderRelease: (_e, g) => {
        if (Math.hypot(g.dx, g.dy) < DRAG_THRESHOLD) {
          navigate.current();
          return;
        }
        const b = bounds.current;
        const y = Math.min(Math.max(last.current.y + g.dy, b.minY), b.maxY);
        const droppedX = Math.min(Math.max(last.current.x + g.dx, b.minX), b.maxX);
        const snapX = droppedX + fabWidth / 2 < width / 2 ? b.minX : b.maxX;
        last.current = { x: snapX, y };
        Animated.spring(pan, {
          toValue: { x: snapX, y },
          useNativeDriver: false,
          friction: 7,
          tension: 80,
        }).start();
      },
    }),
  ).current;

  const pulseStyle = {
    opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.28, 0] }),
    transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.3] }) }],
  };

  return (
    <Animated.View
      {...panResponder.panHandlers}
      accessibilityRole="button"
      accessibilityLabel="Open Warmup daily games"
      style={[styles.fabBase, compact ? styles.circle : styles.pill, { transform: pan.getTranslateTransform() }]}
    >
      <Animated.View
        pointerEvents="none"
        style={[compact ? styles.pulseRingCircle : styles.pulseRingPill, pulseStyle]}
      />
      <Gamepad2 size={compact ? 26 : 22} color={WARMUP_COLORS.textOnBright} strokeWidth={2.5} />
      {!compact && <Text style={styles.label}>Games</Text>}
      <Animated.View pointerEvents="none" style={styles.badge}>
        <Text style={styles.badgeEmoji}>🔥</Text>
      </Animated.View>
    </Animated.View>
  );
}
